import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Button,
  Box,
  CircularProgress,
} from '@mui/material';
import { colors, fonts, spacing, borderRadius } from '../../constants';

/**
 * AssignDialog Component
 * Dialog for assigning/delegating queries
 */
const AssignDialog = ({
  open,
  onClose,
  query,
  assignUsers,
  selectedAssigneeId,
  onAssigneeChange,
  loadingAssignUsers,
  assigningId,
  onConfirm,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: borderRadius.lg,
          boxShadow: `0 28px 60px ${colors.primaryShadow}`,
          overflow: 'hidden',
          border: `1px solid ${colors.primaryTint}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          px: spacing.md,
          py: spacing.sm,
          background: colors.primaryGradient,
          color: colors.textWhite,
          borderBottom: 'none',
          boxShadow: `0 18px 32px ${colors.primaryShadow}`,
        }}
      >
        <Typography
          variant="h5"
          component="div"
          sx={{
            fontWeight: fonts.weightBold,
            fontSize: '1.5rem',
            letterSpacing: '-0.4px',
          }}
        >
          Delegate Query
        </Typography>
      </DialogTitle>
      <DialogContent
        sx={{
          px: spacing.md,
          py: spacing.lg,
          display: 'flex',
          flexDirection: 'column',
          gap: spacing.md,
          background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 40%)',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: colors.textPrimaryDark,
            fontSize: fonts.fontSizeSm,
            lineHeight: 1.6,
            pt: spacing.sm,
          }}
        >
          Choose the delegate who should receive the latest context and status updates for this query.
        </Typography>
        <FormControl
          fullWidth
          size="small"
          disabled={loadingAssignUsers || assignUsers.length === 0}
        >
          <Select
            value={selectedAssigneeId}
            onChange={onAssigneeChange}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return 'Assign To';
              }
              const user = assignUsers.find((option) => option._id === selected);
              if (!user) return selected;
              const displayName = user.full_name || user.name || user.email || selected;
              return `${displayName} - ${(user.designation || user.role || '').toUpperCase()} (${user.region || 'NA'})`;
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: '300px',
                  overflowY: 'auto',
                },
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
            }}
            sx={{
              fontSize: fonts.fontSizeSm,
              backgroundColor: colors.backgroundWhite,
              borderRadius: borderRadius.input,
              border: `1.5px solid ${colors.borderDefault}`,
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&:hover': {
                borderColor: colors.borderHover,
              },
              '&.Mui-focused': {
                borderColor: colors.primary,
                boxShadow: '0 0 0 3px rgba(66, 133, 244, 0.16)',
              },
            }}
          >
            <MenuItem value="">
              <em>Select user</em>
            </MenuItem>
            {assignUsers.map((user) => (
              <MenuItem 
                key={user._id} 
                value={user._id}
                sx={{
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  py: spacing.xs,
                  '&:hover': {
                    backgroundColor: colors.primaryTint,
                  },
                }}
              >
                {user.full_name || user.name} - {(user.designation || user.role || '').toUpperCase()} ({user.region || 'NA'})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {loadingAssignUsers && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
            <CircularProgress size={16} sx={{ color: colors.primary }} />
            <Typography
              variant="caption"
              sx={{
                color: colors.textSecondary,
                fontSize: fonts.fontSizeXs,
              }}
            >
              Loading users...
            </Typography>
          </Box>
        )}
        {!loadingAssignUsers && assignUsers.length === 0 && (
          <Typography
            variant="caption"
            sx={{
              color: colors.textSecondary,
              fontSize: fonts.fontSizeXs,
            }}
          >
            No assignable users available for this query.
          </Typography>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          borderTop: 'none',
          px: spacing.md,
          py: spacing.sm,
          gap: spacing.xs,
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          boxShadow: '0 -12px 28px rgba(14, 23, 42, 0.12)',
        }}
      >
        <Button
          onClick={onClose}
          disabled={assigningId === query?._id}
          sx={{
            textTransform: 'none',
            fontSize: fonts.fontSizeSm,
            color: colors.primaryDark,
            fontWeight: fonts.weightSemiBold,
            borderColor: colors.borderDefault,
            '&:hover': {
              backgroundColor: colors.primarySoft,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={
            assignUsers.length === 0 ||
            !selectedAssigneeId ||
            assigningId === query?._id
          }
          sx={{
            textTransform: 'none',
            fontSize: fonts.fontSizeSm,
            fontWeight: fonts.weightSemiBold,
            background: colors.primaryGradient,
            color: colors.textWhite,
            minWidth: '110px',
            boxShadow: `0 4px 12px ${colors.primaryShadow}`,
            '&:hover': {
              background: colors.primaryGradientHover,
              boxShadow: `0 6px 16px ${colors.primaryShadowHover}`,
            },
            '&:disabled': {
              backgroundColor: colors.textDisabled,
              color: colors.backgroundWhite,
              boxShadow: 'none',
            },
          }}
        >
          {assigningId === query?._id ? 'Delegating...' : 'Delegate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignDialog;
